import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const useScheduleBuilderUnsavedChanges = ({
  activeEditScheduleId,
  currentSignature,
  onUnsavedChangesChange,
}) => {
  const [baselineSignature, setBaselineSignature] = useState(null);
  const baselineInitTimerRef = useRef(null);
  const latestSignatureRef = useRef(currentSignature);

  useEffect(() => {
    latestSignatureRef.current = currentSignature;
  }, [currentSignature]);

  const baselineKey = useMemo(
    () => (activeEditScheduleId != null ? `edit:${activeEditScheduleId}` : 'create'),
    [activeEditScheduleId],
  );

  useEffect(() => {
    if (baselineInitTimerRef.current) {
      clearTimeout(baselineInitTimerRef.current);
      baselineInitTimerRef.current = null;
    }
    setBaselineSignature(null);
    baselineInitTimerRef.current = window.setTimeout(() => {
      setBaselineSignature(latestSignatureRef.current);
      baselineInitTimerRef.current = null;
    }, 0);
    return () => {
      if (baselineInitTimerRef.current) {
        clearTimeout(baselineInitTimerRef.current);
        baselineInitTimerRef.current = null;
      }
    };
  }, [baselineKey]);

  const hasUnsavedChanges = useMemo(() => {
    if (baselineSignature == null) return false;
    return baselineSignature !== currentSignature;
  }, [baselineSignature, currentSignature]);

  useEffect(() => {
    onUnsavedChangesChange?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, onUnsavedChangesChange]);

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const resetUnsavedChangesTracking = useCallback(() => {
    if (baselineInitTimerRef.current) {
      clearTimeout(baselineInitTimerRef.current);
      baselineInitTimerRef.current = null;
    }
    setBaselineSignature(null);
    onUnsavedChangesChange?.(false);
  }, [onUnsavedChangesChange]);

  useEffect(() => {
    return () => {
      if (baselineInitTimerRef.current) {
        clearTimeout(baselineInitTimerRef.current);
      }
      onUnsavedChangesChange?.(false);
    };
  }, [onUnsavedChangesChange]);

  return {
    hasUnsavedChanges,
    resetUnsavedChangesTracking,
  };
};
